import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "@radix-ui/react-icons";
import { InfoIcon } from "@phosphor-icons/react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/ds/components/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { ChatShell } from "@/zapi-frontend/shared/ui/chat";
import { TChapter } from "@/zapi-frontend/shared/types/projects";
import { MODEL_CHOOSER_OPTIONS } from "@/zapi-frontend/shared/types/models";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { useSnippetsVersionInfo } from "@/zapi-frontend/shared/hooks/useVersionInfo";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { ChatMessageList, TGenerationProgressProps } from "./ChatMessageList";
import { useChat } from "../hooks/useChat";
import { useFileUpload } from "../hooks/useFileUpload";

type TChatIslandProps = {
  webLinkEnabled?: boolean;
  messageAttachmentsEnabled?: boolean;
  chapter?: TChapter;
  askOnly?: boolean;
  generationProgress?: TGenerationProgressProps;
  headerRight?: ReactNode;
};

export function ChatIsland({
  webLinkEnabled = false,
  messageAttachmentsEnabled = false,
  chapter,
  askOnly,
  generationProgress,
  headerRight
}: TChatIslandProps = {}) {
  const { t } = useTranslation(["projectHome", "common"]);
  const {
    projectId,
    messages,
    isLoading,
    loadingMessageId,
    isGeneratingChapter,
    progressMessage,
    prereqsChaptersToBeGenerated,
    allPrereqChapters,
    hasMessages,
    handleSubmit,
    handleAbortRequest,
    handleClearChat,
    isInitializing,
    isSendingRequest,
    isAbortingRequest,
    isAskStreaming
  } = useChat({ chapter, askOnly });

  const { isLabs, role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const { selectedProject } = useProjectStore();
  const { openFileDialog, attachmentFileInputRef, handleFileInputChange } = useFileUpload(projectId);
  const { modelName, setModelName } = useChatStore();
  const { displayText: versionDisplayText } = useSnippetsVersionInfo();

  const [input, setInput] = useState("");

  const chatEnabled = !!selectedProject?.chatEnabled;
  const disabled = isInitializing || !chatEnabled;

  const submit = () => {
    const messageText = input.trim();
    if (!messageText || isLoading || isSendingRequest || disabled) return;
    setInput("");
    handleSubmit(messageText, isLabs ? modelName : null);
  };

  const placeholder = (() => {
    if (isInitializing) return t("common:loadingChapter");
    return chatEnabled ? t("projectHome:chat.askAnything") : t("projectHome:chat.chatDisabled");
  })();

  return (
    <ChatShell
      appearance="leo"
      className="h-full w-full"
      bodyClassName="p-0 overflow-hidden"
      title={t("projectHome:chat.title")}
      headerRight={headerRight}
      placeholder={placeholder}
      input={input}
      onInputChange={setInput}
      onSubmit={submit}
      isBusy={isLoading || isSendingRequest || disabled}
      isLoading={isInitializing}
      loadingLabel={t("common:loadingMessages")}
      dockCloseLabel={t("projectHome:chat.hide")}
      submitLabel={t("common:submit")}
      inputTestId="chat-input"
      sendTestId="send-message-button"
      clearTestId="clear-chat-button"
      titleAdornment={
        isLabs ? (
          <Popover>
            <PopoverTrigger
              data-testid="chat-info-trigger"
              render={
                <button
                  type="button"
                  aria-label="Chat info"
                  className="flex items-center text-innoveraZinc-400 transition-colors hover:text-innoveraZinc-600"
                />
              }
            >
              <InfoIcon className="size-4" weight="regular" />
            </PopoverTrigger>
            <PopoverContent
              align="center"
              sideOffset={8}
              className="relative flex w-[239px] flex-col gap-1 rounded-lg p-3"
            >
              <span className="pointer-events-none absolute left-1/2 top-0 size-3 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-tl-[2px] border-l border-t border-innoveraZinc-100 bg-white" />
              {versionDisplayText && (
                <FigmaTypography variant="body-2" className="text-innoveraZinc-900" data-testid="version-info">
                  Version: <span className="font-semibold">{versionDisplayText}</span>
                </FigmaTypography>
              )}
              <div className="flex items-center gap-2.5">
                <FigmaTypography variant="body-2" className="text-innoveraZinc-900">
                  Model:
                </FigmaTypography>
                <Select
                  items={MODEL_CHOOSER_OPTIONS}
                  value={modelName || null}
                  onValueChange={(next) => next !== null && setModelName(next)}
                >
                  <SelectTrigger
                    id="model-select"
                    className="h-8 w-[160px] gap-2 px-3 text-sm"
                    data-testid="model-select-trigger"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODEL_CHOOSER_OPTIONS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </PopoverContent>
          </Popover>
        ) : undefined
      }
      controlsLeft={
        <>
          {hasEdit && (
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={openFileDialog}
              disabled={isLoading || disabled}
              data-testid="add-files-button"
            >
              <PlusIcon /> {t("common:addFiles")}
            </Button>
          )}
          <input
            type="file"
            ref={attachmentFileInputRef}
            accept=".pdf,.pptx,.jpg,.jpeg,.png,.txt,.csv,.xlsx,.xls,.xlsm,.docx"
            style={{ display: "none" }}
            multiple
            onChange={handleFileInputChange}
            data-testid="file-input"
          />
        </>
      }
      controls={
        isLoading && !isInitializing && !isSendingRequest ? (
          <Button
            variant="destructive"
            size="sm"
            type="button"
            onClick={handleAbortRequest}
            data-testid="abort-button"
            disabled={isAbortingRequest}
          >
            {isAbortingRequest ? (
              <>
                <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                {t("common:loading")}
              </>
            ) : (
              t("common:cancel")
            )}
          </Button>
        ) : undefined
      }
      onClear={handleClearChat}
      canClear={hasMessages && !isLoading && !disabled}
      clearConfirm={{
        title: t("projectHome:chat.clearThisChat"),
        menuLabel: t("common:clear"),
        confirmText: t("common:clear"),
        cancelText: t("common:cancel"),
        toastText: t("projectHome:chat.chatCleared")
      }}
    >
      <ChatMessageList
        messages={messages}
        loadingMessageId={loadingMessageId}
        isGeneratingChapter={isGeneratingChapter}
        progressMessage={progressMessage}
        prereqsChaptersToBeGenerated={prereqsChaptersToBeGenerated}
        allPrereqChapters={allPrereqChapters}
        isAskStreaming={isAskStreaming}
        webLinkEnabled={webLinkEnabled}
        messageAttachmentsEnabled={messageAttachmentsEnabled}
        generationProgress={generationProgress}
      />
    </ChatShell>
  );
}
